
import * as framework from '../../../framework/export';
import { PjErrorInfo } from '../service/PjErrorInfo';
import { PjServiceResponse } from '../service/PjServiceResponse';
import { HistoryService } from '../../history/service/HistoryService';
import { PjInjectProxy } from '../PjInjectProxy';
import { PjInjectProxyImpl } from '../PjInjectProxyImpl';

/**
 * プロジェクト向け基盤Controller
 */
export abstract class PjBaseController extends framework.BaseController {

    /**
     * フレームワークで使用するサービスのプロキシ
     */
    private pjInjectProxyImpl: PjInjectProxyImpl;

    // アラート
    private alerts: Alert[];

    /**
     * Menuの表示非表示を設定する
     * @param {string} show 表示設定
     */
    protected setMenu(show: boolean): void {
        this.pjInjectProxyImpl.getRootScope()['hideMenu'] = !show;
    }

    /**
     * コンストラクタ
     * パラメータでプロパティの定義を実施
     * @param {angular.ui.IStateParamsService} $stateParams UI Routerのパラメータ
     * @param {ng.translate.ITranslateService} $translate angular-translateのサービス
     * @param {PjInjectProxy} pjInjectProxy フレームワークで使用するサービス
     */
    constructor(protected $stateParams: angular.ui.IStateParamsService,
                protected $translate: ng.translate.ITranslateService,
                protected $location: ng.ILocationService,
                pjInjectProxy: PjInjectProxy) {
        super($stateParams, $translate, pjInjectProxy);
        this.pjInjectProxyImpl = <PjInjectProxyImpl> pjInjectProxy;
        this.alerts = [];
        this.pjInjectProxyImpl.getRootScope()['$validationOptions'] = { debounce: 1500 };
    }

    /**
     * エラーハンドリング処理
     * @param {number} status - HTTPステータスコード
     * @param {pjfw.PjErrorInfo} errorInfo - エラー情報
     * @return ハンドリングの内容 true:エラーハンドリングを実施 false: 業務例外のハンドリングを行う
     */
    protected handleSystemError(status: number, errorInfo: PjErrorInfo): boolean {
        // TODO システムで設計したエラーハンドリング処理を実装
        // 下記はサンプルコード
        switch (status) {
            case 404:
                this.pjInjectProxyImpl.getState().go('ERROR',
                    { code: '404'});
                return true;
            case 500:
                this.pjInjectProxyImpl.getState().go('ERROR',
                    { code: errorInfo.params[0]});
                return true;
            default:
                return false;
        }
    }

    /**
     * 履歴へ記録する処理
     * @param {string} timestamp タイムスタンプ
     * @param {any} data 記録するデータ
     */
    protected addHistory(timestamp: string, data: any): void {
        this.pjInjectProxyImpl.getHistoryService().addHistory(timestamp, data);
    }

    /** 履歴を取得する処理
     * @param {string} timestamp タイムスタンプ
     * @return {ng.IPromise} 履歴から取得したデータ
     */
    protected getHistory(timestamp: string): ng.IPromise<string> {
        let deferred: ng.IDeferred<string> = this.pjInjectProxyImpl.getQ().defer();
        // タイムスタンプから履歴の有無を検索し、履歴があれば返す。無ければ、nullを返す
        if (this.pjInjectProxyImpl.getHistoryService().contains(timestamp)) {
            this.pjInjectProxyImpl.getHistoryService().getHistory(timestamp).then(
                (response: PjServiceResponse<string>) => {
                    deferred.resolve(response.data);
                });
        } else {
            deferred.resolve(null);
        }
        return deferred.promise;
    }

    protected setAlert(messageId: string): void {
        if (!messageId) {
            return;
        }
        let type: string;
        if (messageId.startsWith('error.')) {
            type = 'error';
        } else {
            type = 'info';
        }
        this.alerts.push({ type: type, message: this.$translate.instant(messageId) });
    }

    protected setError(messageId: string): void {
        if (!messageId) {
            return;
        }
        this.alerts.push({ type: 'error', message: this.$translate.instant(messageId) });
    }

    protected clearAlert(): void {
        this.alerts.splice(0, this.alerts.length);
    }

    public catch(errorData: any): void {
        this.clearAlert();
        if (errorData.status === 400) {
            if (errorData.data && errorData.data.messages) {
                for (let i: number = 0; i < errorData.data.messages.length; i++) {
                    this.setError(errorData.data.messages[i]);
                }
            } else {
                this.setAlert('error.0007');
            }
        }
        if (errorData.status === 401) {
            this.$location.url('/');
            this.setAlert('error.0008');
        }
        if (errorData.status === 404) {
            this.setAlert('error.0008');
        }
        if (errorData.status === 409) {
            this.setAlert('error.0013');
        }
        if (errorData.status === 500) {
            this.setAlert('message.0005');
        }
    }

    public isTohmatsu(): boolean {
        let auth: any = this.pjInjectProxyImpl.getSharedService().get('auth');
        if (!auth) {
            return false;
        }
        return (auth.role === 3 || auth.role === 4);
    }

    public isReadonly(): boolean {
        let auth: any = this.pjInjectProxyImpl.getSharedService().get('auth');
        if (!auth) {
            return true;
        }
        return (auth.role === 4);
    }

    public checkAuthenticatedUser(): boolean {
        let auth: any = this.pjInjectProxyImpl.getSharedService().get('auth');
        if (!auth) {
            this.$location.url('/');
            return false;
        }
        return true;
    }
}

/**
 * アラート情報
 */
export interface Alert {
    /**
     * Type
     */
    type: string;

    /**
     * メッセージ
     */
    message: string;
}

export class SharedService {

    public get (key: string): any {
        return JSON.parse(window.sessionStorage.getItem(key));
    }

    public set (key: string, value: any): void {
        return window.sessionStorage.setItem(key, JSON.stringify(value));
    }
}



/** WEBPACK FOOTER **
 ** node_modules/tslint-loader!./src/common/extends_fw/controller/PjBaseController.ts
 **/